import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Navigation, type NavEntry } from "@/components/layout/Navigation";
import { Footer } from "@/components/layout/Footer";
import { EventProvider } from "@/lib/event-context";
import { seededEvents } from "@/data/seed-imam-ali-2026";
import { api } from "@/lib/api-client";
import { moduleRegistry, getEnabledModules } from "@/components/modules/registry";
import type { Event } from "@/lib/event-schema";
import { themeToCssVars } from "@/lib/event-theme";
import { NotFoundView } from "@/routes/NotFound";
import { Container } from "@/components/ui/Container";

type LoadState =
  | { status: "loading" }
  | { status: "ready"; event: Event }
  | { status: "not-found" };

export function EventLayout() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    if (!slug) {
      setState({ status: "not-found" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.events
      .get(slug)
      .then((event) => {
        if (!cancelled) setState({ status: "ready", event });
      })
      .catch(() => {
        // Fallback to bundled seed (local dev without wrangler, or pre-launch).
        const seed = seededEvents[slug];
        if (cancelled) return;
        setState(seed ? { status: "ready", event: seed } : { status: "not-found" });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.status === "not-found") {
    return <NotFoundView />;
  }
  if (state.status === "loading") {
    return <EventLoading />;
  }

  const event = state.event;
  const enabled = getEnabledModules(event);
  const entries: NavEntry[] = [
    { label: "Overview", to: `/${event.slug}`, end: true },
    ...enabled
      .filter((m) => m.id !== "hero")
      .map((m) => {
        const cfg = m.config;
        const label =
          cfg.navLabel ?? moduleRegistry[m.id].defaultNavLabel ?? moduleRegistry[m.id].label;
        return { label, to: `/${event.slug}/${m.id}` };
      }),
  ];

  const themeVars = themeToCssVars(event.theme);

  return (
    <EventProvider event={event}>
      <div style={themeVars as React.CSSProperties | undefined} className="contents">
        <Navigation entries={entries} homePath={`/${event.slug}`} />
        <main className="flex-1">
          <Outlet />
        </main>
        <Footer eventTitle={event.title} />
      </div>
    </EventProvider>
  );
}

function EventLoading() {
  return (
    <div className="flex-1">
      <Container size="wide" className="py-24">
        <div className="animate-pulse space-y-8">
          <div className="h-6 w-44 rounded bg-line/70" />
          <div className="h-20 w-3/4 rounded bg-line/60" />
          <div className="h-5 w-2/3 rounded bg-line/60" />
          <div className="grid gap-6 lg:grid-cols-[1.05fr,0.95fr]">
            <div className="h-32 rounded bg-line/40" />
            <div className="aspect-[4/5] rounded-3xl bg-line/40" />
          </div>
        </div>
      </Container>
    </div>
  );
}
